import { useEffect, useState, useContext } from 'react';
import getConfig, { getBaseUrl } from 'src/utils/getConfig';
import { appModeName } from 'src/utils/capabilities';
import { object, arrayOf, node, oneOfType } from 'prop-types';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { Header } from 'src/components/layout/header';
import TrackingHelper from 'src/utils/trackingHelper';
import { NotificationModal } from 'src/components/common/NotificationModal';
import { LoaderComponent } from 'src/components/common/Loader';
import { isPrivilegedRole } from 'src/utils/authHelper';
import { setDatadogUser } from 'src/utils/datadogErrorHandling';
import { Footer } from 'src/components/layout/Footer';
import { useOnboarding } from 'src/hooks/useOnboarding';
import {
  FORBIDDEN_PAGE_URL,
  UNAUTHORIZED_PAGE_URL,
  DEPRECATED_FEATURE_PAGE,
} from 'src/constants/urlConstants';
import { AppContext } from 'src/context';
import { useSession } from 'src/hooks/useSession';
import { showIntercom } from 'src/utils/intercom';
import { ONBOARDING_STARTED_AGENCY_STATUS } from 'src/constants/agency';
import { AGENT_PROFILE_STATUSES } from 'src/constants/agentProfileStatus';
import { GlobalStyle, MainLayoutContent } from './styles';
import { checkUserAccess } from 'src/utils/checkUserAccess';

const MainLayout = ({ children, location: { pathname } }) => {
  const [trackingProcessed, setTrackingProcessed] = useState(false);
  const { store } = useContext(AppContext);
  const { loading: sessionLoading } = useSession();
  const { loading: onboardingLoading } = useOnboarding(
    pathname,
    store.agency?.status,
  );
  const notificationModals = useSelector(state => state.notificationModals);
  const is401Page = pathname === UNAUTHORIZED_PAGE_URL;
  const isForbiddenPage = pathname === FORBIDDEN_PAGE_URL;
  const is410Page = pathname === DEPRECATED_FEATURE_PAGE;
  const isErrorPage = is401Page || isForbiddenPage || is410Page;
  const isLoading = store.isLoading || sessionLoading || onboardingLoading;

  const isHeaderShown =
    store?.agent?.id &&
    !isErrorPage &&
    isPrivilegedRole(store.agent?.roles) &&
    !isLoading;

  useEffect(() => {
    if (store.agent?.id && store.agency?.id) {
      setDatadogUser(store.agent, store.originalAgent);
    }
  }, [store.agent, store.originalAgent, store.agency?.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const originalLoggedInUserEmail = get(store, 'originalAgent.email', '');
    checkUserAccess(originalLoggedInUserEmail);
    if (originalLoggedInUserEmail !== '' && !trackingProcessed) {
      const traits = {
        applicationMode: appModeName,
        userEmail: originalLoggedInUserEmail,
        userRole: 'c4a_subagent',
        agencyName: get(store, 'agency.name'),
      };
      setTrackingProcessed(true);
      TrackingHelper.identify(originalLoggedInUserEmail, traits);
    }
  });

  const currentAgent = get(store, 'agent');

  useEffect(() => {
    const isShowIntercom =
      !isLoading &&
      !!currentAgent?.agency?.status &&
      currentAgent?.agency?.status !== ONBOARDING_STARTED_AGENCY_STATUS;

    if (isShowIntercom) {
      showIntercom(currentAgent);
    }
  }, [currentAgent, isLoading]);

  if (currentAgent?.agent_profile?.status === AGENT_PROFILE_STATUSES.churned) {
    window.location.assign(getBaseUrl() + FORBIDDEN_PAGE_URL);
    return null;
  }

  const isShowChildren = (!isLoading && store.agent?.id) || isErrorPage;

  return (
    <>
      <GlobalStyle />
      {isLoading && !isErrorPage && <LoaderComponent subtitle="" />}
      {isHeaderShown && (
        <Header
          config={getConfig()}
          originalAgent={store.originalAgent}
          currentAgent={store.agent}
        />
      )}
      <NotificationModal
        isOpen={notificationModals.isSuccessOpen}
        modalType="success"
        title="common.successfulOperation"
        confirmButtonName="common.accept"
      />
      <NotificationModal
        isOpen={notificationModals.isErrorOpen}
        modalType="error"
        title="common.errorOperation"
        subTitle="common.tryAgain"
        confirmButtonName="common.close"
      />
      <MainLayoutContent show={!is401Page && !isForbiddenPage}>
        {isShowChildren && children}
      </MainLayoutContent>
      <Footer />
    </>
  );
};

MainLayout.propTypes = {
  children: oneOfType([arrayOf(node), node]),
  location: object,
};

export { MainLayout };
